/**
 * S3 — THE only file of the web app that calls the network (eslint.config.js, measured in
 * test/repo-rules/lint-boundaries.test.ts).
 *
 * Every answer that is not 2xx carries the one error shape of the API
 * (docs/06-api-contract §1.1): `{ statusCode, error, message, field? }`, message already in
 * Vietnamese and meant to be shown as is. This file turns that into an `ApiError` and adds the
 * two cases the API cannot answer for itself:
 *
 * - 401 ⇒ `signedOut`: the caller sends the reader to /login instead of drawing an error;
 * - ⚠️ 502/503/504 ⇒ `serverDown`: the DEV PROXY answers with a valid response when the API is
 *   not running (docs/00-project-rules/07 §4), so a dead API looks like a normal HTTP answer,
 *   not a connection failure. Same for nginx in production.
 *
 * `fetchImpl` is injected so the tests can measure all of that without a network or a browser.
 */
export interface ApiErrorBody {
  statusCode: number;
  error: string;
  message: string;
  field?: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly field?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The session is gone (or was never there): sign in again, do not draw an error box. */
  get signedOut(): boolean {
    return this.status === 401;
  }

  /** The API process is not answering — a message about the server, not about the request. */
  get serverDown(): boolean {
    return this.status === 502 || this.status === 503 || this.status === 504;
  }
}

export const SERVER_DOWN_MESSAGE = 'Máy chủ không trả lời, vui lòng thử lại sau';
export const NETWORK_MESSAGE = 'Không kết nối được máy chủ, kiểm tra đường mạng';

/** A query value: a scalar, or a list when the parameter repeats (`?flag=A&flag=B`). */
export type QueryValue = string | number | boolean | null | undefined | readonly string[];
export type Query = Readonly<Record<string, QueryValue>>;

/**
 * `{ from: '2026-01-01', flag: ['VN', 'PA'], q: undefined }` ⇒ `?from=2026-01-01&flag=VN&flag=PA`.
 * Empty values are dropped so a cleared filter never reaches the API as `?q=`.
 */
const isList = (value: QueryValue): value is readonly string[] => Array.isArray(value);

export function queryString(query: Query): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === '') continue;
    if (isList(value)) for (const item of value) search.append(key, item);
    else search.append(key, String(value));
  }
  const text = search.toString();
  return text === '' ? '' : `?${text}`;
}

export interface RequestOptions {
  readonly query?: Query;
  readonly body?: unknown;
  readonly signal?: AbortSignal;
  readonly fetchImpl?: typeof fetch;
}

async function errorFrom(response: Response): Promise<ApiError> {
  let body: Partial<ApiErrorBody> | undefined;
  try {
    body = (await response.json()) as Partial<ApiErrorBody>;
  } catch {
    body = undefined;
  }
  const fallback =
    response.status >= 502 && response.status <= 504 ? SERVER_DOWN_MESSAGE : response.statusText;
  const message =
    typeof body?.message === 'string' && body.message !== '' ? body.message : fallback;
  return new ApiError(response.status, message, body?.field);
}

async function request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
  const call = options.fetchImpl ?? fetch;
  const hasBody = options.body !== undefined;
  let response: Response;
  try {
    response = await call(`${path}${queryString(options.query ?? {})}`, {
      method,
      credentials: 'same-origin',
      headers: hasBody ? { 'Content-Type': 'application/json' } : undefined,
      body: hasBody ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(0, NETWORK_MESSAGE);
  }
  if (!response.ok) throw await errorFrom(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const apiGet = <T>(path: string, options?: RequestOptions) =>
  request<T>('GET', path, options);
export const apiPost = <T>(path: string, options?: RequestOptions) =>
  request<T>('POST', path, options);
export const apiPatch = <T>(path: string, options?: RequestOptions) =>
  request<T>('PATCH', path, options);
export const apiPut = <T>(path: string, options?: RequestOptions) =>
  request<T>('PUT', path, options);
export const apiDelete = <T>(path: string, options?: RequestOptions) =>
  request<T>('DELETE', path, options);
